import {ApplicationUserRole, OperationType} from "../api";

const roleTranslations: Record<ApplicationUserRole, string> = {
    [ApplicationUserRole.Admin]: "Administrator",
    [ApplicationUserRole.ProductManager]: "Zarzadzanie produktami",
    [ApplicationUserRole.OrderManager]: "Zarzadzanie zamowieniami",
    [ApplicationUserRole.UserManager]: "Zarzadzanie uzytkownikami",
    [ApplicationUserRole.CanReadWarehouse]: "Dostep do magazynow",
    [ApplicationUserRole.CanUpdateContractors]: "Zarzadzanie kontrahentami",
    [ApplicationUserRole.ManageUsers]: "Zarzadzanie uzytkownikami",
    [ApplicationUserRole.ReadWarehouses]: "Podglad magazynow",
    [ApplicationUserRole.ManageWarehouses]: "Zarzadzanie magazynami",
    [ApplicationUserRole.ReadStock]: "Podglad stanow magazynowych",
    [ApplicationUserRole.ManageStock]: "Zarzadzanie stanami magazynowymi",
    [ApplicationUserRole.ReadProducts]: "Podgląd produktów",
    [ApplicationUserRole.ManageProducts]: "Zarzadzanie produktami",
    [ApplicationUserRole.ReadProduction]: "Podglad produkcji",
    [ApplicationUserRole.ManageProduction]: "Zarzadzanie produkcja",
    [ApplicationUserRole.ReadAnalytics]: "Podglad analiz",
    [ApplicationUserRole.ManageAnalytics]: "Zarzadzanie analizami",
    [ApplicationUserRole.ReadContractors]: "Podglad kontrahentow",
    [ApplicationUserRole.ManageContractors]: "Zarzadzanie kontrahentami",
    [ApplicationUserRole.ReadMeetings]: "Podglad spotkan",
    [ApplicationUserRole.ManageMeetings]: "Zarzadzanie spotkaniami",
    [ApplicationUserRole.ManageSettings]: "Zarzadzanie ustawieniami",
    [ApplicationUserRole.ReadOperations]: "Podglad operacji",
    [ApplicationUserRole.ManageOperations]: "Zarzadzanie operacjami",
    [ApplicationUserRole.ReadInventory]: "Podglad inwentaryzacji",
    [ApplicationUserRole.ManageInventory]: "Zarzadzanie inwentaryzacja",
    [ApplicationUserRole.NexoSync]: "Synchronizacja Nexo"
};

const compatibilityRoleAliases: Partial<Record<ApplicationUserRole, ApplicationUserRole[]>> = {
    [ApplicationUserRole.ProductManager]: [ApplicationUserRole.ManageProducts],
    [ApplicationUserRole.ManageProducts]: [ApplicationUserRole.ProductManager],
    [ApplicationUserRole.OrderManager]: [ApplicationUserRole.ManageStock],
    [ApplicationUserRole.ManageStock]: [ApplicationUserRole.OrderManager],
    [ApplicationUserRole.ManageUsers]: [ApplicationUserRole.UserManager],
    [ApplicationUserRole.UserManager]: [ApplicationUserRole.ManageUsers],
    [ApplicationUserRole.ReadWarehouses]: [ApplicationUserRole.CanReadWarehouse],
    [ApplicationUserRole.CanReadWarehouse]: [ApplicationUserRole.ReadWarehouses],
    [ApplicationUserRole.ManageContractors]: [ApplicationUserRole.CanUpdateContractors],
    [ApplicationUserRole.CanUpdateContractors]: [ApplicationUserRole.ManageContractors]
};

const hiddenCompatibilityRoles = new Set<ApplicationUserRole>([
    ApplicationUserRole.ProductManager,
    ApplicationUserRole.OrderManager,
    ApplicationUserRole.UserManager,
    ApplicationUserRole.CanReadWarehouse,
    ApplicationUserRole.CanUpdateContractors
]);

export const MANAGE_USERS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageUsers, ApplicationUserRole.UserManager];
export const READ_WAREHOUSES_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadWarehouses, ApplicationUserRole.CanReadWarehouse];
export const MANAGE_WAREHOUSES_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageWarehouses];
export const READ_STOCK_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadStock];
export const READ_PRODUCTS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadProducts];
export const MANAGE_PRODUCTS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageProducts, ApplicationUserRole.ProductManager];
export const READ_PRODUCTION_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadProduction];
export const MANAGE_PRODUCTION_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageProduction];
export const READ_ANALYTICS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadAnalytics];
export const MANAGE_ANALYTICS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageAnalytics];
export const READ_CONTRACTORS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadContractors, ApplicationUserRole.ManageContractors, ApplicationUserRole.CanUpdateContractors];
export const MANAGE_CONTRACTORS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageContractors, ApplicationUserRole.CanUpdateContractors];
export const READ_OPERATIONS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadOperations];
export const MANAGE_OPERATIONS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageOperations];
export const MANAGE_ORDER_ROLES: ApplicationUserRole[] = [
    ApplicationUserRole.Admin,
    ApplicationUserRole.ManageStock,
    ApplicationUserRole.OrderManager,
    ApplicationUserRole.ManageOperations
];
export const READ_INVENTORY_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ReadInventory];
export const MANAGE_INVENTORY_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageInventory];
export const MANAGE_SETTINGS_ROLES: ApplicationUserRole[] = [ApplicationUserRole.Admin, ApplicationUserRole.ManageSettings];

const allAuthScopes = [
    "manage:users",
    "read:warehouses",
    "manage:warehouses",
    "read:stock",
    "manage:stock",
    "read:products",
    "manage:products",
    "read:production",
    "manage:production",
    "read:analytics",
    "manage:analytics",
    "read:contractors",
    "manage:contractors",
    "read:meetings",
    "manage:meetings",
    "manage:settings",
    "read:operations",
    "manage:operations",
    "read:inventory",
    "manage:inventory",
    "nexo:sync",
    "email",
    "profile"
];

const assignableApplicationUserRoles = Object.values(ApplicationUserRole).filter(role =>
    role !== ApplicationUserRole.Admin && !hiddenCompatibilityRoles.has(role)
);

const operationTypeTranslations: { operation: OperationType, translation: string, isReservation?: boolean }[] = [
    {operation: OperationType.Create, translation: "utworzyl(a)"},
    {operation: OperationType.Edit, translation: "zmienil(a)"},
    {operation: OperationType.Delete, translation: "usunel(a)"},
    {operation: OperationType.Assign, translation: "przypisal(a)"},
    {operation: OperationType.Assign, translation: "zarezerwowal(a)", isReservation: true}
];

export const normalizeApplicationUserRoles = (
    roles?: ApplicationUserRole[] | null
): ApplicationUserRole[] => {
    const normalizedRoles = new Set<ApplicationUserRole>(roles ?? []);

    for (const role of roles ?? []) {
        const aliases = compatibilityRoleAliases[role] ?? [];
        aliases.forEach(alias => normalizedRoles.add(alias));
    }

    if (normalizedRoles.has(ApplicationUserRole.Admin)) {
        normalizedRoles.add(ApplicationUserRole.UserManager);
        normalizedRoles.add(ApplicationUserRole.CanReadWarehouse);
        normalizedRoles.add(ApplicationUserRole.CanUpdateContractors);
    }

    return Array.from(normalizedRoles);
};

export const getAssignableApplicationUserRoles = (): ApplicationUserRole[] => assignableApplicationUserRoles;

export const getAuth0ScopesForFrontend = (): string => allAuthScopes.join(" ");

const applicationUserRoleTranslationHelper = (role: ApplicationUserRole) =>
    roleTranslations[role] || "";

const operationTypeTranslationHelper = (operation: OperationType, isReservation?: boolean) => {
    return operationTypeTranslations.find(x => x.operation === operation && x.isReservation === isReservation)?.translation || "";
};

export default applicationUserRoleTranslationHelper;

export {operationTypeTranslationHelper};
